import { sortBy, thru, times } from 'lodash'
import moment from 'moment/moment'
import { useMemo, useState } from 'react'

import { ComputeFactory } from '../../models/ComputeFactory'
import { Gift } from '../../models/Gift'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { Subscription } from '../../models/Subscription'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import { SelectModal } from '../common/SelectModal'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { Chip } from '../common/fields/Chip'
import { DatePicker } from '../common/fields/DatePicker'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import {
    findSubscriptionFrequencySelection,
    subscriptionFrequencyOptions,
} from '../settings/Options'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'

type Props = {
    subscription: Subscription
    onUpdateSubscription: (updatedSubscription: Subscription) => void
    owner: User
    subscriptionItems: Item[]
}

export const NewSubscriptionPage = ({
    subscription,
    onUpdateSubscription,
    owner,
    subscriptionItems,
}: Props) => {
    const [clientFetchState, setClientFetchState] = useState<
        { type: 'pending' } | { type: 'fetching' } | { type: 'fetched'; gifts: Gift[] }
    >({ type: 'pending' })
    const [isGiftsSelectModalOpen, setIsGiftsSelectModalOpen] = useState(false)
    const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false)
    const [isSalesTaxModalOpen, setIsSalesTaxModalOpen] = useState(false)

    const handleSelectClient = async (clientUser: User | undefined) => {
        const updatedSubscription = subscription.clone({ client: clientUser })
        if (clientUser === undefined) {
            onUpdateSubscription(updatedSubscription)
            return
        }

        setClientFetchState({ type: 'fetching' })

        const gifts = await ComputeFactory.getInstance().fetchPaidGifts(clientUser, owner)

        setClientFetchState({ type: 'fetched', gifts })

        if (subscription.channel === 'sms' && !clientUser.phone) {
            updatedSubscription.channel = 'email'
        }
        else if (subscription.channel === 'email' && !clientUser.email) {
            updatedSubscription.channel = 'sms'
        }

        onUpdateSubscription(updatedSubscription)

        const client = await RecordFactory.getInstance().loadClient(clientUser.id ?? '')
        if (!client) {
            return
        }

        const clientUpdatedSubscription = updatedSubscription.clone()
        if (owner.allowDiscount && client.discount && client.discountType === 'percent') {
            clientUpdatedSubscription.discount = client.discount
            clientUpdatedSubscription.discountType = 'percent'
            clientUpdatedSubscription.channel = client.notify ?? clientUpdatedSubscription.channel
        }
        if (client.notifyType) {
            clientUpdatedSubscription.channel = client.notifyType
            clientUpdatedSubscription.reminder = client.reminder
        }

        onUpdateSubscription(clientUpdatedSubscription)
    }

    const subscriptionsSearchableItems = useMemo(() => {
        return sortBy(
            subscriptionItems,
            (subscriptionItem) => subscriptionItem.name?.toLowerCase() ?? ''
        ).map((subscriptionItem) => ({
            id: subscriptionItem.id ?? '',
            title: subscriptionItem.name ?? '',
            subtitle:
                subscriptionItem.rate === undefined ? (
                    'TBD'
                ) : (
                    <>
                        <span
                            className={
                                subscriptionItem.discountAmount !== undefined ? 'line-through' : ''
                            }
                        >
                            {currencyFormat(subscriptionItem.rate)}
                        </span>{' '}
                        {subscriptionItem.rate !== undefined &&
                        subscriptionItem.discountAmount !== undefined
                            ? currencyFormat(
                                  subscriptionItem.rate - subscriptionItem.discountAmount
                              )
                            : ''}
                    </>
                ),
            tertiaryView:
                subscriptionItem.image?.uri !== undefined ? (
                    <img src={subscriptionItem.image.uri} className="size-9 rounded-full" />
                ) : (
                    <Icon icon="refresh.small" background="round" className="size-9" />
                ),
            subscriptionItem,
        }))
    }, [subscriptionItems])
    const selectedSubscriptionSearchableItem =
        subscription.plan !== undefined
            ? thru(
                  subscriptionsSearchableItems.find(
                      (searchableItem) => searchableItem.id === subscription.plan?.id
                  ),
                  (item) => {
                      if (item === undefined) {
                          return undefined
                      }
                      const newItem = { ...item }
                      delete (newItem as any).subtitle
                      return {
                          ...newItem,
                          tertiaryView:
                              item.subscriptionItem.image?.uri !== undefined ? (
                                  <img
                                      src={item.subscriptionItem.image.uri}
                                      className="size-6 rounded-full"
                                  />
                              ) : (
                                  <Icon
                                      icon="refresh.small"
                                      background="round"
                                      className="size-6"
                                  />
                              ),
                      }
                  }
              )
            : null
    const handleSelectSubscriptionItem = (subscriptionItem: Item | null) => {
        onUpdateSubscription(
            subscription.clone({
                plan: subscriptionItem ?? undefined,
                memo: subscriptionItem?.memo,
                deposit: subscriptionItem?.deposit,
                total: subscriptionItem?.rate,
                interval: subscriptionItem?.interval,
                instances: subscriptionItem?.instances === 0 ? '' : subscriptionItem?.instances,
                frequency: subscriptionItem?.frequency,
                ...(owner.allowSalesTax && subscriptionItem?.allowSalesTax && owner.taxcode
                    ? { nexus: 'pro', tax: owner.taxcode.rate, taxcode: owner.taxcode }
                    : { nexus: undefined, tax: undefined, taxcode: undefined }),
                ...(owner.allowDiscount &&
                subscriptionItem?.discount &&
                subscriptionItem.discountType === 'percent'
                    ? {
                          discount: subscriptionItem.discount,
                          discount_type: subscriptionItem.discountType,
                          discount_instances: subscriptionItem.discountInstances,
                      }
                    : {}),
                form:
                    owner.allowRecord && subscriptionItem?.form
                        ? subscriptionItem.form
                        : subscription.form,
                contract:
                    owner.allowContract && subscriptionItem?.contract
                        ? subscriptionItem.contract
                        : subscription.contract,
            })
        )
    }

    const selectedFrequencyID = (
        subscription.interval !== undefined && subscription.frequency !== undefined
            ? (findSubscriptionFrequencySelection(subscription.interval, subscription.frequency) ??
              subscriptionFrequencyOptions[0])
            : subscriptionFrequencyOptions[0]
    ).value
    const handleSelectFrequency = (option: { id: string; title: string } | null) => {
        if (option === null) {
            return
        }

        onUpdateSubscription(
            subscription.clone({
                interval: parseInt(option.id.substring(0, option.id.length - 1)),
                frequency:
                    option.id.substring(option.id.length - 1) === 'd'
                        ? 'daily'
                        : option.id.substring(option.id.length - 1) === 'w'
                          ? 'weekly'
                          : 'monthly',
            })
        )
    }

    const giftItems = useMemo(() => {
        if (clientFetchState.type !== 'fetched') {
            return null
        }

        return clientFetchState.gifts.map((gift) => ({
            id: gift.id ?? '',
            title: gift.item?.name ?? '',
            subtitle: gift.item?.rate === undefined ? 'TBD' : currencyFormat(gift.item.rate),
            gift,
        }))
    }, [clientFetchState])
    const selectedGiftItem = (giftItems ?? []).find(
        (item) => item.gift.id === subscription.gift?.id
    )
    const handleSelectGiftItems = (giftItems: { gift: Gift }[]) => {
        setIsGiftsSelectModalOpen(false)

        const gift = giftItems[0]?.gift
        if (gift === undefined) {
            onUpdateSubscription(subscription.clone({ gift: undefined }))
        }
        else {
            onUpdateSubscription(subscription.clone({ gift }))
        }
    }

    const receipt = useMemo((): Receipt => {
        return {
            modifiers: {
                subtotal: {
                    amount: subscription.total ?? 0,
                    onUpdate: (newAmount) =>
                        onUpdateSubscription(subscription.clone({ total: newAmount })),
                },
                setupFee: {
                    amount: subscription.deposit !== undefined ? subscription.deposit : null,
                    onChange: (newValue) =>
                        onUpdateSubscription(subscription.clone({ deposit: newValue })),
                },
                giftCertificate: owner.allowGift
                    ? {
                          selectedGift: subscription.gift,
                          onSelectGift: (gift) => {
                              if (gift !== undefined) {
                                  onUpdateSubscription(
                                      subscription.clone({
                                          gift,
                                          discount_type: 'rate',
                                          discount: gift.balance ?? 0,
                                      })
                                  )
                              }
                            else {
                                  onUpdateSubscription(subscription.clone({ gift: undefined }))
                              }
                          },
                      }
                    : undefined,
                discount: owner.allowDiscount
                    ? {
                          discountInstances: {
                              selectedID: subscription.discountInstances?.toString() ?? '',
                              onUpdateID: (id) =>
                                  onUpdateSubscription(
                                      subscription.clone({ discount_instances: id })
                                  ),
                          },
                      }
                    : undefined,
                salesTax: owner.allowSalesTax
                    ? {
                          amount:
                              subscription.taxcode !== undefined
                                  ? (subscription.taxAmount ?? null)
                                  : null,
                          parenthetical:
                              subscription.taxcode !== undefined
                                  ? `${subscription.taxcode.rate ?? 0}%`
                                  : null,
                      }
                    : undefined,
                surcharge: owner.allowSurcharge
                    ? {
                          amount: subscription.allowSurcharge
                              ? (subscription.surchargeAmount ?? null)
                              : null,
                          onAdd: () =>
                              onUpdateSubscription(subscription.clone({ allow_surcharge: true })),
                          onRemove: () =>
                              onUpdateSubscription(subscription.clone({ allow_surcharge: false })),
                      }
                    : undefined,
            },
            total: subscription.chargeAmount,
        }
    }, [subscription])

    return (
        <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
            <WorkflowPage.Card title="General">
                <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                    <ClientSearchableSelect
                        mode="server"
                        selectedClient={subscription.client}
                        selectedSubaccount={subscription.subaccount}
                        onSelectClient={handleSelectClient}
                        onSelectSubaccount={(subaccount) =>
                            onUpdateSubscription(
                                subscription.clone({ client: subaccount?.client, subaccount })
                            )
                        }
                        shouldPreserveLastSelection={true}
                        canAddNew
                        shouldIncludeSubaccounts
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Plan (required)" icon="refresh.large">
                    <SearchableSelect
                        items={subscriptionsSearchableItems}
                        onSelectItem={(item) =>
                            handleSelectSubscriptionItem(item?.subscriptionItem ?? null)
                        }
                        selectedItemOverride={selectedSubscriptionSearchableItem}
                        titleWeight="bold"
                        placeholder="Type to search"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Schedule">
                <WorkflowPage.IconSection
                    title="Start on"
                    icon="calendar.large"
                    description="Select when the subscription starts"
                >
                    <DatePicker
                        size="medium"
                        selectedDateString={
                            subscription.date?.format('MM/DD/YYYY') ?? moment().format('MM/DD/YYYY')
                        }
                        onSelectDate={(newDate) => {
                            onUpdateSubscription(
                                subscription.clone({
                                    date: moment(newDate),
                                })
                            )
                        }}
                        minDateString={moment().format('MM/DD/YYYY')}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Frequency"
                    icon="refresh.large"
                    description="Set a payment schedule"
                >
                    <SearchableSelect
                        items={subscriptionFrequencyOptions.map((option) => ({
                            id: option.value,
                            title: option.label,
                        }))}
                        selectedItemID={selectedFrequencyID}
                        onSelectItem={handleSelectFrequency}
                        size="medium"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Number of payments"
                    icon="hash.large"
                    description="Select the number of payments"
                >
                    <SearchableSelect
                        items={[
                            { id: '', title: 'Continuous' },
                            ...times(52, (index) => ({
                                id: (index + 1).toString(),
                                title: (index + 1).toString(),
                            })),
                        ]}
                        onSelectItem={(item) =>
                            onUpdateSubscription(subscription.clone({ instances: item?.id }))
                        }
                        selectedItemID={subscription.instances?.toString() ?? ''}
                        size="medium"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Total">
                <div>
                    <WorkflowPage.ReceiptView
                        receipt={receipt}
                        transaction={subscription}
                        onUpdateTransaction={onUpdateSubscription}
                        client={subscription.client}
                        owner={owner}
                    />

                    {giftItems !== null && (
                        <SelectModal
                            isOpen={isGiftsSelectModalOpen}
                            onClose={() => setIsGiftsSelectModalOpen(false)}
                            selectionType="single"
                            items={giftItems}
                            selectedItems={selectedGiftItem !== undefined ? [selectedGiftItem] : []}
                            onSelectItems={handleSelectGiftItems}
                            title="Select Gift Certificate"
                        />
                    )}

                    <WorkflowPage.DiscountModal
                        isOpen={isDiscountModalOpen}
                        onClose={() => setIsDiscountModalOpen(false)}
                        discountType={subscription.discountType === 'rate' ? 'rate' : 'percent'}
                        discountAmount={subscription.discount ?? 0}
                        onChange={(newType, newAmount) => {
                            onUpdateSubscription(
                                subscription.clone({
                                    discount_type: newType,
                                    discount: newAmount,
                                })
                            )
                        }}
                    />

                    <WorkflowPage.SalesTaxModal
                        isOpen={isSalesTaxModalOpen}
                        onClose={() => setIsSalesTaxModalOpen(false)}
                        nexus={
                            subscription.nexus === 'pro'
                                ? 'pro'
                                : subscription.nexus === 'transaction'
                                  ? 'transaction'
                                  : ''
                        }
                        ownerTaxcode={owner.taxcode}
                        taxcode={subscription.taxcode}
                        onChange={(newNexus, newTaxcode) =>
                            onUpdateSubscription(
                                subscription.clone({
                                    nexus: newNexus,
                                    taxcode: newTaxcode,
                                    tax: newTaxcode?.rate,
                                })
                            )
                        }
                    />
                </div>
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Notifications" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Send via"
                    icon="messageArrowUp.large"
                    description="Choose how you'd like your client to be sent this subscription"
                >
                    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                        <Chip
                            title="Don't notify"
                            isSelected={(subscription.channel ?? '') === ''}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ channel: '' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="Text message"
                            isSelected={subscription.channel === 'sms'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ channel: 'sms' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="Text and email"
                            isSelected={subscription.channel === 'combo'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ channel: 'combo' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="Email"
                            isSelected={subscription.channel === 'email'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ channel: 'email' }))
                            }
                            size="small"
                        />
                    </div>
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Payment reminder"
                    icon="moneyAlarmClock.large"
                    description="Choose when you'd like your client to receive a reminder ahead of each renewal"
                >
                    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-2">
                        <Chip
                            title="Don't remind"
                            isSelected={(subscription.reminder ?? '') === ''}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ reminder: '' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="24 hours"
                            isSelected={subscription.reminder === '1'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ reminder: '1' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="2 days"
                            isSelected={subscription.reminder === '2'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ reminder: '2' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="3 days"
                            isSelected={subscription.reminder === '3'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ reminder: '3' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="5 days"
                            isSelected={subscription.reminder === '5'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ reminder: '5' }))
                            }
                            size="small"
                        />
                        <Chip
                            title="7 days"
                            isSelected={subscription.reminder === '7'}
                            onClick={() =>
                                onUpdateSubscription(subscription.clone({ reminder: '7' }))
                            }
                            size="small"
                        />
                    </div>
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>

            <WorkflowPage.Card title="More options" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Memo"
                    icon="note.large"
                    secondaryText={`${(subscription.memo ?? '').length} / 1500`}
                >
                    <TextArea
                        size="medium"
                        isAutoResizing
                        placeholder="Add a memo"
                        className="overlow-y-hidden"
                        value={subscription.memo ?? ''}
                        onChange={(event) =>
                            onUpdateSubscription(subscription.clone({ memo: event.target.value }))
                        }
                        maxLength={1500}
                        onClear={() => onUpdateSubscription(subscription.clone({ memo: '' }))}
                    />
                </WorkflowPage.IconSection>

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a form" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={subscription.form}
                            onSelectForm={(form) =>
                                onUpdateSubscription(subscription.clone({ form }))
                            }
                        />
                    </WorkflowPage.IconSection>
                )}

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={subscription.contract}
                            onSelectContract={(contract) =>
                                onUpdateSubscription(subscription.clone({ contract }))
                            }
                        />
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>
        </div>
    )
}
